import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { Formik, Form, Field } from "formik";
import { brandRegistrationValidation } from "@/components/CustomComponents/BrandRegistration/validation";
import ReCaptcha from "@/utils/reCaptchaApi";
import {
  verifyForm,
  formSubmission,
} from "@/services/formSubmitAPI/client";
import { useRouter } from "next/router"

const captchaDisabled = process.env.NEXT_PUBLIC_RECAPTCHA_DISABLE === "0";
// ── Helpers ─────────────────────────────────────────────
function fieldBorderClass(name, errors, touched) {
  return errors[name] && touched[name] ? "border-red-500" : "border-gray-300";
}

function ErrorMsg({ name, errors, touched }) {
  if (!errors[name] || !touched[name]) return null;
  return (
    <p
      id={`${name}-error`}
      role="alert"
      className="text-red-500 text-sm mt-1.5"
    >
      {errors[name]}
    </p>
  );
}

// ── Component ───────────────────────────────────────────
export default function BrandRegistration({ brandRegistrationData }) {
  const [submitError, setSubmitError] = useState(null);
  const router = useRouter()
  const {
    heading,
    subheading,
    requiredNote,
    submitLabel,
    submittingLabel,
    fields,
  } = brandRegistrationData;

  const initialValues = {
    projectType: [],
    projectAs: "",
    budgetRange: "",
    timelineProject: "",
    projectLocation: "",
    customerName: "",
    customerEmail: "",
    phoneNumber: "",
    agreeTerms: false,
    agreeMarketing: false,
    captchaToken: "dev-token",
  };

  // useEffect(() => {
  //   if (captchaDisabled) return;
  //   const script = document.createElement("script");
  //   script.src = "https://www.google.com/recaptcha/api.js";
  //   script.async = true;
  //   document.body.appendChild(script);
  //   return () => document.body.removeChild(script);
  // }, []);

  async function handleSubmit(
    values,
    { setSubmitting, setFieldError, setStatus }
  ) {
    setSubmitError(null);
    try {
      if (captchaDisabled) {
        const requestId = await formSubmission(values);
        if(requestId){
            router.push("/brand-registration-thankyou")
        }
        return;
      }
      if (!values.captchaToken) {
        setFieldError("captchaToken", "Please verify the captcha");
        return;
      }

      const verifyData = await verifyForm(values.captchaToken);
      if(verifyData &&
      verifyData?.message === "Verified" &&
      verifyData?.data?.success === true &&
      verifyData?.data?.score >= process.env["THRESHOLD_SCORE"]) {
        const { captchaToken, ...payload } = values;
        const requestId = await formSubmission(payload);
        if(requestId){
            router.push("/brand-registration-thankyou")
        }
      }
      else {
        setFieldError("captchaToken", "Captcha verification failed");
        return;
      }
    } catch (err) {
      console.error(err);
      setSubmitError("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="mx-auto max-w-[900px] px-6 py-12">
      {/* Header */}
      <div className="text-center mb-4">
        <h2 className="text-[42px] md:text-[52px] font-light text-gray-900">
          {heading}
        </h2>
        <p className="mt-3 text-gray-700">{subheading}</p>
      </div>

      {/* Required note */}
      <div className="text-right text-sm text-gray-500 mb-6">
        {requiredNote}
      </div>

      <Formik
        initialValues={initialValues}
        validationSchema={brandRegistrationValidation}
        onSubmit={handleSubmit}
      >
        {({
          values,
          errors,
          touched,
          setFieldValue,
          setFieldTouched,
          isSubmitting,
        }) => (
          <Form className="space-y-8">
            {/* 🔥 Dynamic Fields */}
            {fields?.map(field => {
              if (!field) return null;

              // ── Checkbox Group ──
              if (field.type === "checkbox-group") {
                return (
                  <div key={field.name}>
                    <label className="block mb-3 text-sm font-medium">
                      {field.label}{" "}
                      {field.required && (
                        <span className="text-red-500">*</span>
                      )}
                    </label>

                    <div className={`flex flex-wrap gap-3`}>
                      {field.options?.map(option => {
                        const checked = values[field.name]?.includes(option);

                        return (
                          <label
                            key={option}
                            className={`flex items-center gap-[10px] px-[16px] py-[10px] rounded-full cursor-pointer transition ${
                              checked ? "bg-[#e6e6e6]" : "bg-[#f0efef]"
                            }`}
                          >
                            <input
                              type="checkbox"
                              className="hidden"
                              checked={checked}
                              onChange={() => {
                                const current = values[field.name] || [];

                                const next = checked
                                  ? current.filter(v => v !== option)
                                  : [...current, option];

                                setFieldValue(field.name, next, true);
                                setFieldTouched(field.name, true, false);
                              }}
                            />

                            {/* Circle */}
                            <span
                              className={`w-[18px] h-[18px] rounded-full flex items-center justify-center border transition ${
                                checked
                                  ? "bg-black border-black"
                                  : "border-[#bebebe] bg-white"
                              }`}
                            >
                              <svg
                                className={`w-[10px] h-[10px] transition ${
                                  checked ? "opacity-100" : "opacity-0"
                                }`}
                                viewBox="0 0 16 16"
                                fill="none"
                              >
                                <path
                                  d="M3 8.5L6.5 12L13 5"
                                  stroke="white"
                                  strokeWidth="2"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                              </svg>
                            </span>

                            <span className="text-[16px] text-black">
                              {option}
                            </span>
                          </label>
                        );
                      })}
                    </div>

                    <ErrorMsg
                      name={field.name}
                      errors={errors}
                      touched={touched}
                    />
                  </div>
                );
              }

              // ── Select ──
              if (field.type === "select") {
                return (
                  <div key={field.name}>
                    <label className="block mb-2 text-sm font-medium">
                      {field.label}{" "}
                      {field.required && (
                        <span className="text-red-500">*</span>
                      )}
                    </label>

                    <Field
                      as="select"
                      name={field.name}
                      className={`w-full border rounded px-4 py-3 ${fieldBorderClass(field.name, errors, touched)}`}
                    >
                      <option value="">{field.placeholder}</option>
                      {field.options?.map(opt => (
                        <option key={opt} value={opt}>
                          {opt}
                        </option>
                      ))}
                    </Field>

                    <ErrorMsg
                      name={field.name}
                      errors={errors}
                      touched={touched}
                    />
                  </div>
                );
              }

              // ── Single Checkbox ──
              if (field.type === "checkbox") {
                return (
                  <div key={field.name}>
                    <label className="flex gap-2 items-start text-sm">
                      <Field type="checkbox" name={field.name} />
                      {field.label}
                    </label>

                    <ErrorMsg
                      name={field.name}
                      errors={errors}
                      touched={touched}
                    />
                  </div>
                );
              }

              // ── Input ──
              return (
                <div key={field.name}>
                  <label className="block mb-2 text-sm font-medium">
                    {field.label}{" "}
                    {field.required && <span className="text-red-500">*</span>}
                  </label>

                  <Field
                    type={field.type}
                    name={field.name}
                    placeholder={field.placeholder}
                    className={`w-full border rounded px-4 py-3 ${fieldBorderClass(field.name, errors, touched)}`}
                  />

                  <ErrorMsg
                    name={field.name}
                    errors={errors}
                    touched={touched}
                  />
                </div>
              );
            })}
            {/* reCAPTCHA */}
            {!captchaDisabled && (
              <div>
                <div
                  className={
                    errors.captchaToken && touched.captchaToken
                      ? "[&_iframe]:border-2 [&_iframe]:border-red-500 [&_iframe]:rounded"
                      : ""
                  }
                >
                  <ReCaptcha
                    siteKey={process.env.NEXT_PUBLIC_RECAPTCHA_V2_SITE_KEY}
                    onChange={token => {
                      setFieldValue("captchaToken", token || "", true);
                      setFieldTouched("captchaToken", true, true);
                    }}
                    onExpired={() => setFieldValue("captchaToken", "")}
                    onError={() => setFieldValue("captchaToken", "")}
                  />
                </div>

                <ErrorMsg
                  name="captchaToken"
                  errors={errors}
                  touched={touched}
                />
              </div>
            )}

            {/* Submit Error */}
            {submitError && (
              <div className="text-red-600 text-sm text-center">
                {submitError}
              </div>
            )}

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full bg-black text-white py-4 uppercase text-sm tracking-wider disabled:opacity-60"
            >
              {isSubmitting ? submittingLabel : submitLabel}
            </button>
          </Form>
        )}
      </Formik>
    </div>
  );
}

// ── PropTypes ─────────────────────────────────────────────
BrandRegistration.propTypes = {
  brandRegistrationData: PropTypes.object.isRequired,
};
